import * as THREE from 'three'
import { types, getParent, IAnyModelType, Instance } from 'mobx-state-tree'
import { generateType } from 'src/api/models/utils/generateNodeType'
import {
  accessoryConnection,
  nuts,
  nutsProperties,
  feets,
  feetsEdge,
  feetsProperties,
  screws,
  screwsProperties,
  screwsEdge,
  nutsEdge
} from 'src/constants/typenames'
import { screw, nut, foot } from 'src/constants/models'
import { Project } from 'src/api/models/Project'
import { front, right, bottom, left, back } from 'src/constants/sides'
import { Model } from 'src/api/misc/Model'
import { getSideTranslation, Translation } from 'src/utils/getTranslation'

const screwSize = 8

export const ScrewsProperties = generateType(screwsProperties)
  .props({
    model: types.optional(types.reference(Model), screw)
  })
  .views(self => {
    let model: THREE.BufferGeometry
    const PromisedModel = self.model.instance()

    const arrangeModel = (geometry: THREE.BufferGeometry) => {
      if (!geometry.boundingBox) geometry.computeBoundingBox()
      const { min, max } = geometry.boundingBox as THREE.Box3
      const scale =
        (1 /
          [max.x - min.x, max.y - min.y, max.z - min.z].sort((a, b) =>
            a > b ? 1 : a === b ? 0 : -1
          )[0]) *
        screwSize
      geometry.scale(scale, scale, scale)
      geometry.center()
      geometry.computeVertexNormals()
      model = geometry
    }

    if (PromisedModel instanceof Promise) {
      PromisedModel.then(arrangeModel)
    } else if (PromisedModel instanceof THREE.BufferGeometry) {
      arrangeModel(PromisedModel)
    }
    return {
      modelInstance() {
        if (model === undefined) {
          throw PromisedModel
        } else {
          return model
        }
      }
    }
  })

export const Screws = generateType(screws).props({
  properties: types.optional(ScrewsProperties, {})
})

const generateAccessoryEdge = (model: IAnyModelType) =>
  model.views(self => {
    return {
      get properties() {
        const project = getParent<Instance<ReturnType<typeof Project>>>(self, 3)
        const placements: {
          translation: Translation
        }[] = project.enclosure.adapterEdges[0].node.plates.edges.reduce(
          (p, edge) => [
            ...p,
            ...edge.node.heads.edges.map(headEdge => {
              const v = new THREE.Vector3(...headEdge.properties.screwPosition)
              return {
                translation: getSideTranslation(
                  headEdge.properties.globalSide,
                  new THREE.Box3(v, v)
                )
              }
            })
          ],
          [] as { translation: Translation }[]
        )
        return {
          placements
        }
      }
    }
  })

export const ScrewsEdge = generateAccessoryEdge(
  generateType(screwsEdge).props({ node: types.optional(Screws, {}) })
)

const NutsProperties = generateType(nutsProperties)
  .props({
    model: types.optional(types.reference(Model), nut)
  })
  .views(self => {
    let model: THREE.BufferGeometry
    const PromisedModel = self.model.instance()

    const arrangeModel = (geometry: THREE.BufferGeometry) => {
      if (!geometry.boundingBox) geometry.computeBoundingBox()
      geometry.center()
      const size = new THREE.Vector3()
      geometry.boundingBox?.getSize(size)
      geometry.translate(0, 0, size.z / -2)
      geometry.rotateX(Math.PI / -2)
      geometry.rotateY(Math.PI / 2)
      geometry.computeVertexNormals()
      model = geometry
    }

    if (PromisedModel instanceof Promise) {
      PromisedModel.then(arrangeModel)
    } else if (PromisedModel instanceof THREE.BufferGeometry) {
      arrangeModel(PromisedModel)
    }
    return {
      modelInstance() {
        if (model === undefined) {
          throw PromisedModel
        } else {
          return model
        }
      }
    }
  })

export const Nuts = generateType(nuts).props({
  properties: types.optional(NutsProperties, {})
})

export const NutsEdge = generateAccessoryEdge(
  generateType(nutsEdge).props({
    node: types.optional(Nuts, {})
  })
)

const FeetsProperties = generateType(feetsProperties)
  .props({
    model: types.optional(types.reference(Model), foot)
  })
  .views(self => {
    let model: THREE.BufferGeometry
    const PromisedModel = self.model.instance()

    const arrangeModel = (geometry: THREE.BufferGeometry) => {
      if (!geometry.boundingBox) geometry.computeBoundingBox()
      geometry.center()
      const size = new THREE.Vector3()
      geometry.boundingBox?.getSize(size)
      geometry.translate(0, size.y / 2, 0)
      geometry.computeVertexNormals()
      model = geometry
    }

    if (PromisedModel instanceof Promise) {
      PromisedModel.then(arrangeModel)
    } else if (PromisedModel instanceof THREE.BufferGeometry) {
      arrangeModel(PromisedModel)
    }
    return {
      modelInstance() {
        if (model === undefined) {
          throw PromisedModel
        } else {
          return model
        }
      }
    }
  })

export const Feets = generateType(feets).props({
  properties: types.optional(FeetsProperties, {})
})

export const FeetsEdge = generateType(feetsEdge)
  .props({
    node: types.optional(Feets, {})
  })
  .views(self => {
    return {
      get properties() {
        const project = getParent<Instance<ReturnType<typeof Project>>>(self, 3)
        const cover = project.enclosure.coverEdges[0].node
        const translation = getSideTranslation(bottom, cover.boundings)
        const x1 =
          project.enclosure.adapterEdges[0].node.plates.edges[0].node.heads.feetsData.left.first[0]
        const x2 =
          project.enclosure.adapterEdges[0].node.plates.edges[0].node.heads.feetsData.left.last[0]
        const z1 =
          project.enclosure.adapterEdges[0].node.plates.edges[0].node.heads.feetsData.bottom
            .first[2]
        const z2 =
          project.enclosure.adapterEdges[0].node.plates.edges[0].node.heads.feetsData.bottom.last[2]
        return {
          placements: [
            [x1, z1],
            [x1, z2],
            [x2, z1],
            [x2, z2]
          ].map(([x, z]) => ({
            translation: { ...translation, position: [x, cover.boundings.min.y, z] }
          }))
        }
      }
    }
  })

export const AccessoryConnection = generateType(accessoryConnection)
  .props({
    edges: types.optional(
      types.array(types.union(ScrewsEdge, NutsEdge, FeetsEdge)),
      [screwsEdge, nutsEdge, feetsEdge].map(__typename => ({ __typename }))
    )
  })
  .views(self => {
    return {
      get screws() {
        return self.edges.find(edge => edge.__typename === screwsEdge) as Instance<
          typeof ScrewsEdge
        >
      },
      get nuts() {
        return self.edges.find(edge => edge.__typename === nutsEdge) as Instance<typeof NutsEdge>
      },
      get feets() {
        return self.edges.find(edge => edge.__typename === feetsEdge) as Instance<typeof FeetsEdge>
      }
    }
  })
